/**
 * UsableChatEmbed — PostMessage bridge between an Office task pane and the
 * embedded Usable Chat iframe. Host-agnostic: shared by every Office add-in.
 *
 * Aligned to the Usable Chat embed contract (v1.162.0) and extended with the
 * stateless parent-persistence surface used for local chat history:
 *   iframe→parent: CONVERSATION_CREATED, MESSAGE_CREATED, CONVERSATION_RENAMED,
 *                  CONVERSATION_MESSAGES_UPSERTED
 *   parent→iframe: UPSERT_CONVERSATION_MESSAGES
 *
 * Design notes:
 * - Single window message listener per instance; the bound handler is stored so
 *   `destroy()` can remove it (losing this leaks a listener per mount and causes
 *   the WKWebView double-delivery seen in Office task panes).
 * - Strict origin + source validation; lifecycle events never accept `*`.
 * - Tool-call dedup keyed on requestId (survives double-mounts / re-delivery).
 * - Auth token cached so it can be re-sent on REQUEST_TOKEN_REFRESH.
 * - `addImage()` is an opt-in host capability (PowerPoint); hosts that don't
 *   need it simply never call it.
 */

// ---------------------------------------------------------------------------
// Stateless parent-persistence payload types (contract v1.161.0+)
// ---------------------------------------------------------------------------

export type ConversationMessagesUpsertMode = "append-or-replace-by-id" | "replace-all";

/**
 * A message as exported/restored by the embed. Intentionally permissive: `parts`
 * (tool-invocation state, args/input, output), `attachments`, and any other
 * JSON-safe fields are preserved verbatim so a restored message can round-trip
 * back into tool-aware history. Do NOT narrow this — losing fields breaks restore.
 */
export interface ExportedMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content?: string;
  parts?: unknown[];
  attachments?: unknown[];
  createdAt?: string;
  [key: string]: unknown;
}

export interface StatelessConversationPayload {
  conversationId: string;
  title?: string;
  stateless: true;
  createdAt?: string;
  updatedAt?: string;
}

export interface StatelessMessageCreatedPayload {
  conversationId: string;
  kind: "user" | "assistant";
  message: ExportedMessage;
}

export interface ConversationRenamedPayload {
  conversationId: string;
  title?: string;
}

export interface ConversationMessagesUpsertPayload {
  conversationId?: string | null;
  messages: ExportedMessage[];
  mode?: ConversationMessagesUpsertMode;
}

export interface ConversationMessagesUpsertedPayload {
  ok: boolean;
  conversationId?: string | null;
  messageCount?: number;
  mode?: ConversationMessagesUpsertMode;
  error?: string;
}

// ---------------------------------------------------------------------------
// Tool types
// ---------------------------------------------------------------------------

export interface ParentToolSchema {
  name: string;
  description: string;
  parameters?: {
    type: "object";
    properties: Record<string, unknown>;
    required?: string[];
  };
}

export interface ToolCallPayload {
  requestId: string;
  tool: string;
  args: unknown;
}

/**
 * A runtime context entry — host-pushed text injected into the embed's system
 * prompt as `[key]: content` under a "Context Entries" heading (embed contract;
 * see Usable fragment 74e66de6). Used to tell the assistant what the user is
 * currently looking at (selected range, open email, current slide) so "fix this"
 * / "what's wrong here" resolves without the user pasting anything.
 */
export interface ContextEntry {
  key: string;
  content: string;
  metadata?: Record<string, unknown>;
}

export type ToolCallHandler = (
  tool: string,
  args: unknown,
  requestId: string
) => Promise<unknown>;

/**
 * An AI-generated image pushed from the iframe (IMAGE_GENERATED, embed
 * contract; Usable fragment 9e16baa4). Emitted whenever the embed's
 * generate-image tool succeeds — the parent receives the bytes directly, so
 * hosts never need to fetch chat file URLs cross-origin (which CORS blocks).
 */
export interface GeneratedImagePayload {
  fileId: string;
  base64: string;
  mimeType: string;
  prompt?: string;
  aspectRatio?: string;
}

/** A file staged in the embed (paperclip upload). From `data_list`. */
export interface StagedFileEntry {
  alias: string;
  fileName: string;
  mimeType?: string;
  sizeBytes?: number;
  format?: string;
  [key: string]: unknown;
}

/** Raw bytes of one staged file. From `data_get_bytes`. */
export interface StagedFileBytes {
  alias: string;
  fileName: string;
  format?: string;
  mimeType?: string;
  sizeBytes?: number;
  base64: string;
}

/**
 * Parent-side surface of the embed's data-staging RPC (usable-chat v1.167.0;
 * fragment 014e810b). Gated by `features.dataStaging.exposeToParent` on the
 * embed config — calls reject when the gate is off.
 */
export interface StagedDataApi {
  list(): Promise<{ files: StagedFileEntry[] }>;
  readBytes(alias: string): Promise<StagedFileBytes>;
}

/** A normal chat attachment (paperclip upload). From `attachments_list`. */
export interface AttachmentEntry {
  fileId: string;
  fileName: string;
  mimeType?: string;
  sizeBytes?: number;
  createdAt?: string;
  [key: string]: unknown;
}

/** Original bytes of one chat attachment. From `attachment_get_bytes`. */
export interface AttachmentBytes {
  fileId: string;
  fileName: string;
  mimeType?: string;
  sizeBytes?: number;
  base64: string;
}

/**
 * Parent-side surface for NORMAL chat attachments (usable-chat v1.171.0;
 * fragment 65d80036) — distinct from data staging: uploads stay on the
 * multimodal path (the model can still SEE images) while the parent reads
 * their bytes. Gated by `features.attachments.exposeToParent`; reads are
 * scoped to stored attachments of the current conversation.
 */
export interface AttachmentsApi {
  list(): Promise<{ files: AttachmentEntry[] }>;
  readBytes(fileId: string): Promise<AttachmentBytes>;
}

/** Bytes of a file stored in a Usable WORKSPACE. From `workspace_file_get_bytes`. */
export interface WorkspaceFileBytes {
  fileId: string;
  fileName: string;
  mimeType?: string;
  sizeBytes?: number;
  base64: string;
}

/**
 * Parent-side surface for USABLE WORKSPACE files (usable-chat v1.172.0;
 * board task fc75bc23, solution a5ebd3d2) — the third leg after data staging
 * and attachments: durable workspace assets (brand logos etc.) the chat's own
 * search-files can discover. Gated by `features.workspaceFiles.exposeToParent`;
 * reads are scoped to what the embed session can already access.
 */
export interface WorkspaceFilesApi {
  readBytes(fileId: string): Promise<WorkspaceFileBytes>;
  /**
   * Durable upload from the parent (board task 09120133; wire name
   * `fragment_file_put_bytes` proposed — NOT the rotating temp store). Rejects
   * until the usable-chat bridge ships / the gate is on.
   */
  putBytes(fileName: string, mimeType: string, base64: string): Promise<{ fileId: string }>;
}

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

export interface UsableChatEmbedOptions {
  /** Expected origin of the iframe (e.g. "https://chat.usable.dev"). Use "*" to skip validation (never in prod). */
  iframeOrigin?: string;
  onToolCall?: ToolCallHandler;
  onError?: (code: string, message: string) => void;
  onConversationChange?: (conversationId: string | null) => void;
  /** Called when the embed requests a fresh token. Return the new access token, or null on failure. */
  onTokenRefreshRequired?: () => Promise<string | null>;

  // --- Stateless parent-persistence callbacks ---
  /** A new stateless conversation/session was created (fires on first user turn). */
  onConversationCreated?: (payload: StatelessConversationPayload) => void;
  /** A user or assistant message was created in the stateless conversation. */
  onMessageCreated?: (payload: StatelessMessageCreatedPayload) => void;
  /** The stateless conversation title changed (title-sync). */
  onConversationRenamed?: (payload: ConversationRenamedPayload) => void;
  /** Acknowledgement of a parent-driven `upsertConversationMessages` restore. */
  onConversationMessagesUpserted?: (payload: ConversationMessagesUpsertedPayload) => void;

  /** The embed's generate-image tool produced an image (bytes included). */
  onImageGenerated?: (payload: GeneratedImagePayload) => void;

  /**
   * Raw tap on every validated iframe→parent message (after origin/source
   * checks, before dispatch). Feeds a host's opt-in dev inspector.
   */
  onMessage?: (type: string, payload: unknown) => void;
}

export class UsableChatEmbed {
  private iframe: HTMLIFrameElement;
  private iframeOrigin: string;
  private options: UsableChatEmbedOptions;
  private readyCallbacks: Array<() => void> = [];
  private isReady = false;
  private cachedToken: string | null = null;
  private handledRequestIds = new Set<string>();
  private messageListener: (event: MessageEvent) => void;
  private pendingDataRequests = new Map<
    string,
    { resolve: (result: Record<string, unknown>) => void; reject: (err: Error) => void; timeout: ReturnType<typeof setTimeout> }
  >();

  constructor(iframe: HTMLIFrameElement, options: UsableChatEmbedOptions = {}) {
    this.iframe = iframe;
    this.iframeOrigin = options.iframeOrigin ?? "*";
    this.options = options;

    this.messageListener = this.handleMessage.bind(this);
    window.addEventListener("message", this.messageListener);
  }

  // ---------------------------------------------------------------------------
  // Callbacks
  // ---------------------------------------------------------------------------

  onReady(callback: () => void): void {
    if (this.isReady) {
      callback();
    } else {
      this.readyCallbacks.push(callback);
    }
  }

  // ---------------------------------------------------------------------------
  // Commands (parent → iframe)
  // ---------------------------------------------------------------------------

  setAuth(token: string): void {
    this.cachedToken = token;
    this.postToIframe({ type: "AUTH", payload: { token } });
  }

  registerTools(tools: ParentToolSchema[]): void {
    this.postToIframe({ type: "REGISTER_TOOLS", payload: { tools } });
  }

  /** Remove previously registered parent tools by name (embed contract). */
  deregisterTools(toolNames: string[]): void {
    this.postToIframe({ type: "DEREGISTER_TOOLS", payload: { toolNames } });
  }

  setConfig(config: unknown): void {
    this.postToIframe({ type: "CONFIG", payload: config });
  }

  /**
   * Runtime context entries — a per-key text store appended to the embed's
   * system prompt (embed contract; Usable fragment 74e66de6). Re-sending the
   * same key overwrites it. Envelope mirrors `registerTools` (`payload.{tools}`);
   * if entries ever stop landing in the prompt, reconcile against usable-chat
   * `src/lib/embed-sdk.ts`.
   */
  setContextEntries(entries: ContextEntry[]): void {
    this.postToIframe({ type: "SET_CONTEXT_ENTRIES", payload: { entries } });
  }

  removeContextEntries(keys: string[]): void {
    this.postToIframe({ type: "REMOVE_CONTEXT_ENTRIES", payload: { keys } });
  }

  clearContextEntries(): void {
    this.postToIframe({ type: "CLEAR_CONTEXT_ENTRIES" });
  }

  toggle(visible: boolean): void {
    this.postToIframe({ type: "TOGGLE_VISIBILITY", payload: { visible } });
  }

  newConversation(): void {
    this.postToIframe({ type: "NEW_CONVERSATION" });
  }

  restoreConversation(conversationId: string): void {
    this.postToIframe({ type: "RESTORE_CONVERSATION", payload: { conversationId } });
  }

  /**
   * Opt-in host capability: send an image to the embed (e.g. a slide thumbnail).
   * Accepts a raw base64 string or a `data:` URL — the mime type/base64 are split out.
   *
   * With `submit: true` (embed contract v1.117.0+) the embed stages the image AND
   * submits it as a user turn in the same call, so the model sees it as a
   * multimodal message and reviews it on the next turn — this is the self-verify
   * path (e.g. an AI screenshotting its own slide to check the result). Default
   * `false` only stages the image into the composer for the user to send.
   */
  addImage(
    base64OrDataUrl: string,
    options?: { mimeType?: string; fileName?: string; submit?: boolean }
  ): void {
    let base64 = base64OrDataUrl;
    let mimeType = options?.mimeType ?? "image/png";
    if (base64OrDataUrl.startsWith("data:")) {
      // Tolerate extra parameters between the mime type and ;base64
      // (e.g. "data:image/png;charset=utf-8;base64,…").
      const match = base64OrDataUrl.match(/^data:([^;,]+)[^,]*;base64,(.+)$/);
      if (match) {
        mimeType = match[1];
        base64 = match[2];
      }
    }
    this.postToIframe({
      type: "ADD_IMAGE",
      payload: {
        base64,
        mimeType,
        fileName: options?.fileName ?? "slide.png",
        submit: options?.submit ?? false,
      },
    });
  }

  /**
   * Restore parent-persisted stateless messages into the iframe's local
   * transcript. Default mode `append-or-replace-by-id` for incremental
   * hydration; pass `replace-all` for a full restore-on-load. Resolution is
   * observed via the `onConversationMessagesUpserted` callback.
   */
  upsertConversationMessages(payload: ConversationMessagesUpsertPayload): void {
    this.postToIframe({
      type: "UPSERT_CONVERSATION_MESSAGES",
      payload: {
        conversationId: payload.conversationId ?? null,
        messages: payload.messages,
        mode: payload.mode ?? "append-or-replace-by-id",
      },
    });
  }

  respondToToolCall(requestId: string, result: unknown): void {
    this.postToIframe({
      type: "TOOL_RESPONSE",
      payload: { requestId, result },
    });
  }

  /**
   * Data-staging RPC (embed contract v1.167.0): DATA_REQUEST {requestId, tool,
   * args} → DATA_RESPONSE {requestId, result: {ok, …}}. Only the surface the
   * add-ins need — list staged uploads and pull raw bytes (e.g. a logo the
   * user attached via the paperclip). Gated server-side by the embed config's
   * `features.dataStaging.exposeToParent`.
   */
  readonly data: StagedDataApi = {
    list: () => this.requestData<{ files: StagedFileEntry[] }>("data_list", {}, 30_000),
    readBytes: (alias: string) =>
      this.requestData<StagedFileBytes>("data_get_bytes", { alias }, 90_000),
  };

  /**
   * Normal chat attachments RPC (embed contract v1.171.0, same DATA_REQUEST
   * envelope): the parent reads paperclip uploads WITHOUT diverting them from
   * the multimodal path. Gated by `features.attachments.exposeToParent`.
   */
  readonly attachments: AttachmentsApi = {
    list: () => this.requestData<{ files: AttachmentEntry[] }>("attachments_list", {}, 30_000),
    readBytes: (fileId: string) =>
      this.requestData<AttachmentBytes>("attachment_get_bytes", { fileId }, 90_000),
  };

  /** Workspace-file RPC (embed contract v1.172.0, same DATA_REQUEST envelope). */
  readonly workspaceFiles: WorkspaceFilesApi = {
    readBytes: (fileId: string) =>
      this.requestData<WorkspaceFileBytes>("workspace_file_get_bytes", { fileId }, 90_000),
    putBytes: (fileName: string, mimeType: string, base64: string) =>
      this.requestData<{ fileId: string }>(
        "fragment_file_put_bytes",
        { fileName, mimeType, base64 },
        120_000
      ),
  };

  private requestData<T>(tool: string, args: unknown, timeoutMs: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const requestId =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const timeout = setTimeout(() => {
        if (this.pendingDataRequests.delete(requestId)) {
          reject(new Error(`data request timed out: ${tool}`));
        }
      }, timeoutMs);

      this.pendingDataRequests.set(requestId, {
        resolve: (result) => {
          if (result.ok === true) {
            const { ok: _ok, ...rest } = result;
            resolve(rest as T);
          } else {
            reject(new Error(String(result.error ?? "data request failed")));
          }
        },
        reject,
        timeout,
      });

      this.postToIframe({ type: "DATA_REQUEST", payload: { requestId, tool, args } });
    });
  }

  destroy(): void {
    window.removeEventListener("message", this.messageListener);
    this.pendingDataRequests.forEach(({ reject, timeout }) => {
      clearTimeout(timeout);
      reject(new Error("embed destroyed"));
    });
    this.pendingDataRequests.clear();
  }

  // ---------------------------------------------------------------------------
  // Incoming message handler
  // ---------------------------------------------------------------------------

  private handleMessage(event: MessageEvent): void {
    // Origin validation
    if (this.iframeOrigin !== "*" && event.origin !== this.iframeOrigin) {
      return;
    }
    // Source validation — only accept messages from our iframe
    if (event.source !== this.iframe.contentWindow) {
      return;
    }

    const data = event.data;
    if (!data || typeof data !== "object" || !data.type) {
      return;
    }

    // Surface every validated iframe→parent message to the host's optional tap
    // (feeds a dev inspector — Office task panes have no Inspect Element on Mac).
    this.options.onMessage?.(data.type, data.payload ?? data);

    switch (data.type) {
      // Both READY and EMBED_READY signal readiness; fire callbacks once.
      case "READY":
      case "EMBED_READY":
        if (!this.isReady) {
          this.isReady = true;
          this.readyCallbacks.forEach((cb) => cb());
          this.readyCallbacks = [];
        }
        break;

      case "TOOL_CALL": {
        const payload = data.payload as ToolCallPayload;
        const { requestId, tool, args } = payload;

        // Deduplication — survives double-mounts / duplicated events.
        if (this.handledRequestIds.has(requestId)) {
          return;
        }

        if (this.options.onToolCall) {
          // Mark handled only when we take ownership, so ids never accumulate
          // when no handler is configured (the cleanup timer lives in this branch).
          this.handledRequestIds.add(requestId);
          this.options.onToolCall(tool, args, requestId)
            .then((result) => {
              this.respondToToolCall(requestId, { success: true, result });
            })
            .catch((err: Error) => {
              this.respondToToolCall(requestId, {
                success: false,
                error: err.message ?? String(err),
              });
            })
            .finally(() => {
              // Clean up after 30 s to avoid unbounded set growth
              setTimeout(() => this.handledRequestIds.delete(requestId), 30000);
            });
        }
        break;
      }

      case "REQUEST_TOKEN_REFRESH":
        if (this.options.onTokenRefreshRequired) {
          this.options.onTokenRefreshRequired()
            .then((token) => {
              if (token) this.setAuth(token);
            })
            .catch((err: Error) => {
              this.options.onError?.("TOKEN_REFRESH_FAILED", err?.message ?? String(err));
            });
        } else if (this.cachedToken) {
          this.setAuth(this.cachedToken);
        }
        break;

      case "ERROR":
      case "EMBED_ERROR":
        if (this.options.onError) {
          this.options.onError(data.payload?.code ?? "UNKNOWN", data.payload?.message ?? "");
        }
        break;

      case "CONVERSATION_CHANGED":
        this.options.onConversationChange?.(data.payload?.conversationId ?? null);
        break;

      // --- Stateless parent-persistence lifecycle ---
      case "CONVERSATION_CREATED":
        this.options.onConversationCreated?.(data.payload as StatelessConversationPayload);
        break;

      case "MESSAGE_CREATED":
        this.options.onMessageCreated?.(data.payload as StatelessMessageCreatedPayload);
        break;

      case "CONVERSATION_RENAMED":
        this.options.onConversationRenamed?.(data.payload as ConversationRenamedPayload);
        break;

      case "CONVERSATION_MESSAGES_UPSERTED":
        this.options.onConversationMessagesUpserted?.(
          data.payload as ConversationMessagesUpsertedPayload
        );
        break;

      case "IMAGE_GENERATED":
        this.options.onImageGenerated?.(data.payload as GeneratedImagePayload);
        break;

      case "DATA_RESPONSE": {
        const payload = data.payload as { requestId?: string; result?: Record<string, unknown> };
        if (!payload?.requestId) break;
        const pending = this.pendingDataRequests.get(payload.requestId);
        if (pending) {
          this.pendingDataRequests.delete(payload.requestId);
          clearTimeout(pending.timeout);
          pending.resolve(payload.result ?? { ok: false, error: "empty DATA_RESPONSE" });
        }
        break;
      }

      default:
        break;
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  private postToIframe(message: unknown): void {
    const target = this.iframeOrigin === "*" ? "*" : this.iframeOrigin;
    this.iframe.contentWindow?.postMessage(message, target);
  }
}
